import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import { hotTopics, topicCategories } from "@/mocks/topics";

const sortOptions = [
  { key: "heat", label: "按热度" },
  { key: "date", label: "按最新" },
];

export default function Rank() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [activeCat, setActiveCat] = useState("all");
  const [sortBy, setSortBy] = useState("heat");

  const list = useMemo(() => {
    const k = keyword.trim();
    const filtered = hotTopics.filter((topic) => {
      const matchCat = activeCat === "all" || topic.categorySlug === activeCat;
      const matchKey =
        !k ||
        topic.title.includes(k) ||
        topic.summary.includes(k) ||
        topic.tags.some((tag) => tag.includes(k));
      return matchCat && matchKey;
    });
    return [...filtered].sort((a, b) =>
      sortBy === "heat" ? b.heat - a.heat : a.date < b.date ? 1 : -1
    );
  }, [keyword, activeCat, sortBy]);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "吃瓜榜单",
      url: "https://duopingheyi.cn/rank",
      description: "黑料瓜网吃瓜榜单，按热度与最新时间排列的热议话题列表。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://duopingheyi.cn/" },
        { "@type": "ListItem", position: 2, name: "吃瓜榜单", item: "https://duopingheyi.cn/rank" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "吃瓜榜单话题列表",
      itemListElement: list.map((topic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: topic.title,
        url: `https://duopingheyi.cn/topic/${topic.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="吃瓜榜单 - 黑料瓜网热议话题排行与实时热度榜"
        description="黑料瓜网吃瓜榜单汇总今日瓜与新瓜速递的热议话题排行，支持按分类筛选、关键词搜索与热度排序，实时掌握全网最热娱乐话题动态。"
        keywords="吃瓜榜单,黑料瓜网榜单,热议话题排行,今日瓜排行,新瓜速递"
        path="/rank"
        schema={schema}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "吃瓜榜单" }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
            吃瓜榜单
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里按热度与时间排列当前最受讨论的娱乐话题，每两小时刷新一次。热度只反映讨论规模，不代表对话题真伪的判断。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索瓜名、关键词或标签"
                className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                aria-label="搜索话题"
              />
            </div>
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortBy(opt.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                    sortBy === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                activeCat === "all"
                  ? "bg-secondary-600 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:bg-background-100"
              }`}
            >
              全部分类
            </button>
            {topicCategories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => setActiveCat(cat.slug)}
                className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-sm transition-colors ${
                  activeCat === cat.slug
                    ? "bg-secondary-600 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className={`${cat.icon} h-4 w-4 flex items-center justify-center`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="font-label text-sm text-foreground-500">
            共 <span className="font-bold text-primary-600">{list.length}</span> 条话题
            {keyword.trim() ? ` · 关键词「${keyword.trim()}」` : ""}
          </p>
          {(keyword.trim() || activeCat !== "all") ? (
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActiveCat("all");
              }}
              className="flex items-center gap-1 whitespace-nowrap font-label text-sm text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-close-circle-line h-4 w-4 flex items-center justify-center"></i>
              清除筛选
            </button>
          ) : null}
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((topic, index) => (
              <TopicCard key={topic.id} topic={topic} rank={sortBy === "heat" ? index + 1 : undefined} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line h-6 w-6 flex items-center justify-center"></i>
            </span>
            <p className="font-heading text-lg font-bold text-foreground-900">没找到相关话题</p>
            <p className="text-sm text-foreground-500">
              换个关键词试试，或者
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setActiveCat("all");
                }}
                className="mx-1 text-primary-600 underline"
              >
                查看全部榜单
              </button>
            </p>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-5">
          <span className="font-label text-sm font-bold text-foreground-800">相关分类：</span>
          {topicCategories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:bg-secondary-100"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}